import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { InventoryStatusBadge } from '@/modules/inventory/components/InventoryStatusBadge';
import type { InventoryItem } from '@/modules/inventory/types';
import { listMaintenanceTasksForItem } from '@/modules/maintenance/services/maintenanceRepository';
import {
  MAINTENANCE_STATUS_LABELS,
  MAINTENANCE_TYPE_LABELS,
  type MaintenanceTask,
} from '@/modules/maintenance/types/maintenance';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type ItemMaintenancePanelProps = {
  item: InventoryItem;
};

export function ItemMaintenancePanel({ item }: ItemMaintenancePanelProps) {
  const { hasPermission } = useAuth();
  const canReadMaintenance = hasPermission('maintenance.read');
  const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canReadMaintenance) {
      setLoading(false);
      return;
    }
    void listMaintenanceTasksForItem(item.id)
      .then(setTasks)
      .finally(() => setLoading(false));
  }, [item.id, canReadMaintenance]);

  if (!canReadMaintenance) return null;

  const upcoming = tasks.filter(
    (t) => t.status === 'scheduled' || t.status === 'in_progress',
  );
  const lastRepair = tasks.find(
    (t) => t.type === 'repair' && t.status === 'completed',
  );
  const nextInspection = tasks.find(
    (t) => t.type === 'inspection' && t.status === 'scheduled',
  );

  return (
    <div className="inventory-panel">
      <h2>Mantenimiento operativo</h2>
      <p>
        Estado operativo: <InventoryStatusBadge status={item.status} />
      </p>
      <p className="auth-muted">
        El estado del ítem refleja repair/mantenimiento/inspección activos. La
        disponibilidad temporal por reservas es independiente.
      </p>
      {loading ? <p className="auth-muted">Cargando tareas…</p> : null}
      {!loading && upcoming.length > 0 ? (
        <>
          <h3>Próximas tareas</h3>
          <ul>
            {upcoming.map((t) => (
              <li key={t.id}>
                <Link to={`${modulePaths.maintenance}/${t.id}`}>
                  {t.title}
                </Link>{' '}
                ({MAINTENANCE_TYPE_LABELS[t.type]},{' '}
                {t.scheduledStart} → {t.scheduledEnd})
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {lastRepair ? (
        <p>
          <strong>Última reparación:</strong>{' '}
          <Link to={`${modulePaths.maintenance}/${lastRepair.id}`}>
            {lastRepair.title}
          </Link>{' '}
          completada {lastRepair.completedAt ?? '—'}
        </p>
      ) : null}
      {nextInspection ? (
        <p>
          <strong>Próxima inspección:</strong>{' '}
          <Link to={`${modulePaths.maintenance}/${nextInspection.id}`}>
            {nextInspection.title}
          </Link>{' '}
          ({nextInspection.scheduledStart})
        </p>
      ) : null}
      {tasks.length > 0 ? (
        <>
          <h3>Historial reciente</h3>
          <ul>
            {tasks.slice(0, 5).map((t) => (
              <li key={t.id}>
                <Link to={`${modulePaths.maintenance}/${t.id}`}>
                  {t.title}
                </Link>{' '}
                — {MAINTENANCE_STATUS_LABELS[t.status]} (
                {t.scheduledStart})
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p>
        <Link to={modulePaths.maintenance}>Ver módulo mantenimiento →</Link>
      </p>
    </div>
  );
}
